import React from 'react';
import { useSelector } from 'react-redux';
import { Modal, SystemText } from 'e4shared';
import File from './components/file';
import Web from './components/web';
import Challenger from './components/challenger';
import Content from './components/content';
import Gong from './components/gong';
import OneDrive from './components/oneDrive';
import GoogleDocs from './components/googleDocs';
import HighSpotDL from './components/highspotDL';
import Learning360 from './components/learning360';
import Lessonly from './components/lessonly';
import Showpad from './components/showpad';
import Pulse from './components/pulse';
import GTMBuddy from './components/gtmbuddy';
import Docebo from './components/docebo';
import Accelerate from './components/accelerate';
import { translateCompetency } from 'helpers/translations';
import YouTube from './components/youtube';

import './filePicker.css';
import { apiGet } from 'services';

const contentSources = ['content', 'pitchback', 'blog', 'kb'];

export const FilePicker = ({
    allowedSources,
    id,
    label,
    allowMultiple,
    maxSize,
    folder,
    fileValidation,
    show,
    onClose,
    extensions,
    value,
    mediaOnly,
    onAdd,
    modalInModal,
    profileIds,
    anonymous,
}) => {
    const [competencies, setCompetencies] = React.useState([]);
    const [assets, setAssets] = React.useState([]);
    const [source, setSource] = React.useState('file');
    const [options, setOptions] = React.useState([]);
    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        apiGet('competency', user?.guid, response => {
            setCompetencies(
                response
                    .map(c => translateCompetency(user.language_Code, c))
                    .map(r => ({ ...r, name: r.full_Name }))
                    .sort((a, b) => (a.name > b.name ? 1 : -1))
            );
        });
    }, []);

    React.useEffect(() => {
        setAssets(value);

        if (Array.isArray(value) && value.length === 1) {
            setSource(value[0].source);
        }
    }, [value]);

    React.useEffect(() => {
        let temp = [];

        temp.push({
            label: SystemText('Admin/Forms/FilePicker/SourceOption/Upload'),
            icon: 'pc-icon.png',
            option: 'file',
        });

        temp.push({
            label: SystemText('Admin/Forms/FilePicker/SourceOption/Link'),
            icon: 'www-icon.png',
            option: 'url',
        });

        temp.push({
            label: SystemText('Admin/Forms/FilePicker/SourceOption/Insights'),
            icon: 'richardson-logo_sm.png',
            option: 'content',
        });

        if (hasIntegration(user, '360L')) {
            temp.push({
                label: '360Learning',
                icon: '360learning-icon.png',
                option: 'learning360',
            });
        }

        if (hasIntegration(user, 'ACCELERATE')) {
            temp.push({
                label: SystemText('Admin/Forms/FilePicker/SourceOption/Learn'),
                icon: 'accelerate-icon.png',
                option: 'accelerate',
            });
        }

        if (hasIntegration(user, 'CHALLENGER')) {
            temp.push({
                label: 'Challenger',
                icon: 'challenger-icon.png',
                option: 'challenger',
            });
        }

        if (hasIntegration(user, 'DOCEBO')) {
            temp.push({
                label: 'Docebo',
                icon: 'docebo-icon.png',
                option: 'docebo',
            });
        }

        if (hasIntegration(user, 'GONG')) {
            temp.push({
                label: 'Gong',
                icon: 'gong-icon.png',
                option: 'gong',
            });
        }

        temp.push({
            label: 'Google Drive',
            icon: 'google-drive-icon.png',
            option: 'google',
        });

        if (hasIntegration(user, 'GTM')) {
            temp.push({
                label: 'GTM Buddy',
                icon: 'gtm-icon.png',
                option: 'gtm',
            });
        }

        if (hasIntegration(user, 'HIGHSPOT-DL')) {
            temp.push({
                label: 'HighSpot',
                icon: 'highspot-icon.png',
                option: 'highspot-dl',
            });
        }

        if (hasIntegration(user, 'LESS')) {
            temp.push({
                label: 'Lessonly',
                icon: 'lessonly-icon.png',
                option: 'lessonly',
            });
        }

        temp.push({
            label: 'OneDrive',
            icon: 'onedrive-icon.png',
            option: 'onedrive',
        });

        if (hasIntegration(user, 'PULSE')) {
            temp.push({
                label: 'Pulse',
                icon: 'pulse-icon.png',
                option: 'pulse',
            });
        }

        if (hasIntegration(user, 'SHOWPAD')) {
            temp.push({
                label: 'Showpad',
                icon: 'showpad-icon.png',
                option: 'showpad',
            });
        }

        if (allowedSources?.length > 0) {
            temp = temp.filter(x => allowedSources.some(y => y === x.option));
        }

        setSource(temp[0]?.option);
        setOptions(temp);
    }, [user, allowedSources]);

    const setExtraAssets = (extra, source, isE4Content, remove = null) => {
        if (remove) {
            setAssets(assets.filter(x => x.internal_Id !== remove.internal_Id));
        } else {
            const oldAssets = isE4Content ? [...assets] : [...assets].filter(x => x.source !== source);
            const newAssets = [...extra].map(x => ({ ...x, source }));
            setAssets([...oldAssets, ...newAssets]);
        }
    };

    const onCloseHandler = () => {
        onClose();
        setAssets(value);
    };

    const hasIntegration = (user, ref) => {
        return user?.integrations && user.integrations.some(i => i.ref === ref);
    };

    const sourceOption = ({ label, icon, option }) => {
        const count = assets.filter(
            f => (f && f.source === option) || (f.source?.includes('content-') && f.source?.includes(option))
        )?.length;
        return (
            <button
                key={`option-${option}`}
                id="option-button"
                data-testid="option-button"
                className={`btn btn-block ${source === option ? 'btn-primary' : ''}`}
                onClick={() => setSource(option)}
            >
                <img src={`/dist/img/${icon}`} alt="" loading="lazy" />
                <span>{label}</span>
                {count > 0 && <span className="badge text-xs ml10 bg-orange">{count}</span>}
            </button>
        );
    };

    const footer = (
        <div>
            <button
                className="btn btn-success"
                id="ok-button"
                data-testid="ok-button"
                onClick={() => {
                    onAdd(assets);
                    onClose();
                }}
                disabled={assets.length === 0}
                title={SystemText('Admin/Forms/FilePicker/Footer/Buttons/Attach/Title')}
            >
                {SystemText('Admin/Forms/FilePicker/Footer/Buttons/Attach')}
                {assets.length > 0 && <span className="badge text-xs ml10 bg-orange">{assets.length}</span>}
            </button>
            <button
                className="btn"
                id="cancel-button"
                data-testid="cancel-button"
                onClick={onCloseHandler}
                title={SystemText('Admin/Forms/FilePicker/Footer/Buttons/Cancel/Title')}
            >
                {SystemText('Admin/Forms/FilePicker/Footer/Buttons/Cancel')}
            </button>
        </div>
    );

    const sourceIsE4Content = source => source?.includes('content') || contentSources?.includes(source);

    return (
        <div>
            <Modal
                width="850"
                title={
                    label || allowMultiple
                        ? SystemText('Admin/Forms/FilePicker/Modal/Title/Plural')
                        : SystemText('Admin/Forms/FilePicker/Modal/Title')
                }
                show={show}
                close={onClose}
                footer={footer}
                modalInModal={modalInModal}
            >
                <div key={id} id={id}>
                    <div className="file-picker flex">
                        {allowedSources?.length !== 1 && (
                            <div className="file-options">{options.map(o => sourceOption(o))}</div>
                        )}
                        <div className="flex-grow">
                            {source === 'file' && (
                                <>
                                    <File
                                        extensions={extensions}
                                        allowMultiple={allowMultiple}
                                        maxSize={maxSize}
                                        folder={folder}
                                        fileValidation={fileValidation}
                                        assets={assets.filter(a => a.source === 'file')}
                                        onAdd={assets =>
                                            setExtraAssets(
                                                assets.filter(a => a.name),
                                                'file'
                                            )
                                        }
                                        mediaOnly={mediaOnly}
                                    />
                                </>
                            )}
                            {source === 'url' && (
                                <Web
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'url')}
                                    onAdd={assets => setExtraAssets(assets, 'url')}
                                    mediaOnly={mediaOnly}
                                />
                            )}
                            {sourceIsE4Content(source) && (
                                <Content
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => sourceIsE4Content(a.source))}
                                    setExtraAssets={setExtraAssets}
                                />
                            )}
                            {source === 'gong' && (
                                <Gong
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'gong')}
                                    onAdd={assets => setExtraAssets(assets, 'gong')}
                                />
                            )}
                            {source === 'google' && (
                                <GoogleDocs
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    mediaOnly={mediaOnly}
                                    assets={assets.filter(a => a.source === 'google')}
                                    onAdd={assets => setExtraAssets(assets, 'google')}
                                />
                            )}
                            {source === 'gtm' && (
                                <GTMBuddy
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'gtm')}
                                    onAdd={assets => setExtraAssets(assets, 'gtm')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'accelerate' && (
                                <Accelerate
                                    profileIds={profileIds}
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'accelerate')}
                                    onAdd={assets => setExtraAssets(assets, 'accelerate')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                    anonymous={anonymous}
                                />
                            )}
                            {source === 'challenger' && (
                                <Challenger
                                    profileIds={profileIds}
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'challenger')}
                                    onAdd={assets => setExtraAssets(assets, 'challenger')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                    anonymous={anonymous}
                                />
                            )}
                            {source === 'docebo' && (
                                <Docebo
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'docebo')}
                                    onAdd={assets => setExtraAssets(assets, 'docebo')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'highspot-dl' && (
                                <HighSpotDL
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'highspot-dl')}
                                    onAdd={assets => setExtraAssets(assets, 'highspot-dl')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'learning360' && (
                                <Learning360
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'learning360')}
                                    onAdd={assets => setExtraAssets(assets, 'learning360')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'lessonly' && (
                                <Lessonly
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'lessonly')}
                                    onAdd={assets => setExtraAssets(assets, 'lessonly')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'onedrive' && (
                                <OneDrive
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    mediaOnly={mediaOnly}
                                    assets={assets.filter(a => a.source === 'onedrive')}
                                    onAdd={assets => setExtraAssets(assets, 'onedrive')}
                                />
                            )}
                            {source === 'pulse' && (
                                <Pulse
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'pulse')}
                                    onAdd={assets => setExtraAssets(assets, 'pulse')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'showpad' && (
                                <Showpad
                                    display={source}
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'showpad')}
                                    onAdd={assets => setExtraAssets(assets, 'showpad')}
                                    mediaOnly={mediaOnly}
                                    competencies={competencies}
                                />
                            )}
                            {source === 'youtube' && (
                                <YouTube
                                    allowMultiple={allowMultiple}
                                    folder={folder}
                                    assets={assets.filter(a => a.source === 'youtube')}
                                    onAdd={assets => setExtraAssets(assets, 'youtube')}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default FilePicker;
